import { Mui, TOC, scrollToHash } from "@osu/react-ui";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { generateClient } from 'aws-amplify/api';
import { onHistoryUpdateApplication } from '../subscriptions';
import { graphqlApi } from '../../services/graphql/api';
import { formatDatetime } from '../../util/functions';
import useRoleAuthentication from "../../Common/hooks/useRoleAuthentication";
import {
  useGetApplicationQuery,
  useGetTrainingQuery,
  useSearchApplicationDocumentsQuery,
  useGetCommitteeReviewersQuery,
  useGetExpectedRecommendationQuery,
  useGetTrainingConfigQuery,
  useOverrideRecommendationMutation,
  useSubmitTrainingMutation,
  useSubmitReviewMutation,
  useHoldForExportMutation,
  useHoldForAdminMutation,
  useLazyGetApplicationDocumentQuery,
  useLazyGetApplicationDocumentReadOnlyQuery,
  useLazyGetApplicationTrainingDocumentQuery,
} from "../../services/graphql/api";
import { cleanupApplicantDetail, getDocument as getDocumentSliceAction } from "../../services/graphql/slices/applicantSlice";
import { submitRecommendation, overrideRecommendation as overrideRecommendationAction, toggleHoldForExport as toggleHoldForExportAction, toggleHoldForAdmin as toggleHoldForAdminAction, getTrainingConfig, resetSubmitRecommendation } from "../../services/graphql/slices/applicantActionsSlice";
import { OVERRIDE_RECOMMENDATION, SUBMIT_RECOMMENDATION, HOLD_FOR_EXPORT, HOLD_FOR_ADMIN, FETCH_STUDENT, GET_COMMITTEE_REVIEWERS } from "../../actions-index";
import { ERROR_CONSTS, RECOMMENDATIONS, RECOMMENDATION_HISTORY_UPDATES, SEVERITY_LEVELS, STATUS } from "../../util/constants";
import { validate, exportActionVariable, exportStringArray, formsReducer, sectionIsDisabled } from "../NotesState";
import sections from "../components/sections";

const sortBySortPriority = (arr = []) =>
  arr.sort((a, b) => (a.sortPriority > b.sortPriority ? 1 : -1));

const styles = {
  root: (theme) => ({
    marginBottom: theme.spacing(1),
  }),
  button: (theme) => ({
    marginBottom: theme.spacing(3),
  }),
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  }),
  committee: {
    borderColor: "green",
    color: "green",
  },
  success: (theme) => ({
    padding: theme.spacing(2),
  }),
  expanded: {
    marginBottom: "0 !important",
    "&.Mui-selected": {
      marginBottom: "0 !important"
    }
  },
};

function Applicant(props) {
  let navigate = useNavigate();
  const location = useLocation();
  let decodedApps, encodedApps, nextApp;    
  const searchParams = new URLSearchParams(location.search);
  const submitErrorLabelRef = useRef();

  const todo = searchParams.get("todo");
  const from = searchParams.get("from");

  if(todo) {
    decodedApps = JSON.parse(decodeURIComponent(todo))
    nextApp = decodedApps[0];
    decodedApps.shift();
    encodedApps = encodeURIComponent(JSON.stringify(decodedApps));
  }
  const dispatch = useDispatch();
  const authDetails = useRoleAuthentication();
  const { pathname } = location;

  const [show, setShow] = useState(true);
  const [isOverrideDecision, setIsOverrideDecision] = useState(false);
  const [adminReason, setAdminReason] = useState(""); 
  const [openSuccess, setOpenSuccess] = useState(false);
  const submissionInProgressRef = useRef(false);

  if (authDetails?.UNAUTHORIZED && !openSuccess && !submissionInProgressRef.current) {
    navigate("/unauthorized");
  }
  const { applicantId, committeeId, admitTerm } = useParams();

  // Reset submit state when navigating to a different applicant so stale SUCCESS
  // from a prior submission does not trigger the success dialog immediately.
  useEffect(() => {
    dispatch(resetSubmitRecommendation());
    setOpenSuccess(false);
  }, [applicantId, committeeId, admitTerm, dispatch]);

  // ── Props that were previously passed from the container ──
  const { setPageAppHeader } = props;

  const userId = useSelector((state) => state?.authentication?.user?.osuid);

  const isTraining = pathname?.includes("training");

  // ── RTK Query: fetch application / training data ──
  // Both hooks are always called (rules of hooks) but one is skipped based on isTraining
  const queryArgs = useMemo(() => ({ applicantEmployeeId: applicantId, admitTerm, committeeId }), [applicantId, admitTerm, committeeId]);
  const queryOpts = { refetchOnMountOrArgChange: true };

  const {
    data: applicationResult,
    isLoading: isAppLoading,
    isFetching: isAppFetching,
    isError: isAppError,
  } = useGetApplicationQuery(queryArgs, { ...queryOpts, skip: isTraining });

  const {
    data: trainingResult,
    isLoading: isTrainingLoading,
    isFetching: isTrainingFetching,
    isError: isTrainingError,
  } = useGetTrainingQuery(queryArgs, { ...queryOpts, skip: !isTraining });

  const activeResult = isTraining ? trainingResult : applicationResult;
  const isApplicationLoading = isTraining ? isTrainingLoading : isAppLoading;
  const isApplicationFetching = isTraining ? isTrainingFetching : isAppFetching;
  const isApplicationError = isTraining ? isTrainingError : isAppError;

  // Keep a ref to the last successful studentData so we can fall back to it
  // if a post-submission refetch fails and returns no data.
  const lastGoodStudentDataRef = useRef(undefined);

  const studentData = useMemo(
    () => {
      if (activeResult) {
        // While submission is in progress, ignore refetch failures that would
        // blank the page (e.g. view-applicant-authorizer rejects post-submit).
        if (submissionInProgressRef.current && !activeResult.data) {
          return lastGoodStudentDataRef.current;
        }
        return { status: activeResult.status, data: activeResult.data };
      }
      return undefined;
    },
    [activeResult?.status, activeResult?.data]
  );

  if (studentData?.data) {
    lastGoodStudentDataRef.current = studentData;
  }

  // Sync RTK Query data into the legacy studentData.student Redux slice so that
  // existing selectors (getStudentState) used by Actions dialogs, Menu, etc.
  // continue to work during the RTK Query migration.
  useEffect(() => {
    if (!studentData?.data) return;
    // Don't sync while the success dialog is open — the LOADING dispatch causes
    // a flash in components reading from the legacy slice.
    if (openSuccess) return;
    const datetimePageLoad = 'rtk-query-sync';
    // Prime the slice with LOADING first so the SAS-31 guard allows the SUCCESS dispatch
    dispatch({ type: FETCH_STUDENT, payload: { status: STATUS.LOADING, datetimePageLoad } });
    dispatch({ type: FETCH_STUDENT, payload: { status: STATUS.SUCCESS, data: studentData.data, datetimePageLoad } });
  }, [studentData?.data, dispatch, openSuccess]);

  // ── RTK Query: search documents ──
  const {
    data: applicationDocuments,
    refetch: refetchDocuments,
  } = useSearchApplicationDocumentsQuery(
    { applicantEmployeeId: applicantId, admitTerm, committeeId, isTraining },
    { refetchOnMountOrArgChange: true }
  );

  // ── RTK Query: get committee reviewers (admin only) ──
  const committee = studentData?.data?.demographics?.committeeId;
  const {
    data: reviewersData,
  } = useGetCommitteeReviewersQuery(
    { committeeId: committee },
    { skip: !authDetails.ADMIN || !committee }
  );

  // Sync RTK Query reviewer data into the legacy committeeReviewerReducer slice
  // so that useAutoCompleteReviewers (and other consumers of getCommitteeReviewersState)
  // can display the reviewer list.
  // Note: reviewersData is already the transformed array from transformReviewer() —
  // the RTK Query transformResponse unwraps it before caching.
  useEffect(() => {
    if (!reviewersData) return;
    dispatch({ type: GET_COMMITTEE_REVIEWERS, payload: { status: STATUS.SUCCESS, data: reviewersData } });
  }, [reviewersData, dispatch]);

  // ── RTK Query: get expected recommendation (read-only training) ──
  const userIsReader = studentData?.data?.readers?.find(user => user.readerEmployeeId === userId);
  const readOnly = userIsReader?.status?.includes("#TRAINING_COMPLETE");
  const notifyAdmin = userIsReader?.adminAction === "NOTIFY";

  const {
    data: expectedRecData,
  } = useGetExpectedRecommendationQuery(
    {
      applicantEmployeeId: studentData?.data?.demographics?.applicantEmployeeId,
      committeeId: studentData?.data?.demographics?.committeeId,
      term: studentData?.data?.demographics?.admitTerm?.key,
    },
    {
      skip: !readOnly || !!studentData?.data?.expectedRecommendation
        || !studentData?.data?.demographics?.applicantEmployeeId,
    }
  );

  // ── RTK Query: get training config (admin, non-training) ──
  // Sync RTK Query training config data into the legacy createTrainingReducer slice
  // so that consumers of getTrainingState (e.g., CreateTrainingDialogContent)
  // can access the recommendationOptions.
  const {
    data: trainingConfigData,
  } = useGetTrainingConfigQuery(
    { applicantEmployeeId: applicantId, committeeId, term: admitTerm },
    { skip: isTraining || !authDetails.ADMIN }
  );

  useEffect(() => {
  if (!trainingConfigData) return;
  dispatch(getTrainingConfig({
    status: STATUS.SUCCESS,
    trainingExists: trainingConfigData.trainingExists,
    options: trainingConfigData.options,
  }));
}, [trainingConfigData?.options, dispatch]);

  const trainingConfig = trainingConfigData;
  
  // ── RTK Query: document download via lazy queries ──
  const [fetchAppDoc] = useLazyGetApplicationDocumentQuery();
  const [fetchAppDocReadOnly] = useLazyGetApplicationDocumentReadOnlyQuery();
  const [fetchTrainingDoc] = useLazyGetApplicationTrainingDocumentQuery();

  // ── RTK Query mutations ──
  const [overrideRecommendation] = useOverrideRecommendationMutation();
  const [submitTrainingTrigger] = useSubmitTrainingMutation();
  const [submitReviewTrigger] = useSubmitReviewMutation();
  const [holdForExportTrigger] = useHoldForExportMutation();
  const [holdForAdminTrigger] = useHoldForAdminMutation();

  // ── Legacy selectors still needed for submit state and holdForExport state ──
  const submitState = useSelector((state) => state?.submitDetailsReducer?.submitRecommendation);
  const toggleHoldForExportState = useSelector((state) => state?.toggleHoldForExportReducer?.toggleHoldForExport);
  const documentFiles = useSelector((state) => state?.studentData?.documentFiles);

  const [recHistorySub, setRecHistorySub] = useState(false);

  // ── Subscription: onHistoryUpdateApplication ──
  // Patches the RTK Query cache directly so the Applicant page updates in real-time
  useEffect(() => {
    if (!applicantId) return;

    let subscriptionClient;
    try {
      subscriptionClient = generateClient();
    } catch (e) {
      console.warn('Could not create subscription client', e);
      return;
    }

    const sub = subscriptionClient
      .graphql({
        query: onHistoryUpdateApplication,
        variables: { applicantEmployeeId: applicantId },
      })
      .subscribe({
        next: ({ data }) => {
          // Skip subscription-triggered refetches while a submission is in progress.
          // After submit, the authorizer will reject refetch attempts for non-admin
          // reviewers on adminReview committees, which would blank the page.
          if (submissionInProgressRef.current) return;

          const subscriptionEvent = data?.onHistoryUpdate;
          if (!subscriptionEvent) return;

          const updates = Array.isArray(subscriptionEvent.data)
            ? subscriptionEvent.data
            : [];

          // Some subscription payloads may omit data[] details for certain update types.
          // Invalidate applicant cache to force a refetch and keep the detail screen accurate.
          if (updates.length === 0) {
            dispatch(
              graphqlApi.util.invalidateTags([
                { type: 'Applicant', id: applicantId },
              ])
            );
            return;
          }

          // Determine which RTK Query endpoint to patch
          const endpointName = isTraining ? 'getTraining' : 'getApplication';

          for (const item of updates) {
            const { firstName, lastName, reviewerEmployeeId, stage, recommendation } = item;
            const normalizedRecommendation = `${recommendation ?? ''}`.toLowerCase();
            const isUnassignUpdate =
              normalizedRecommendation.includes('cancel') ||
              normalizedRecommendation.includes('unassign');

            if (isUnassignUpdate) {
              // UNASSIGN — mark reader as cancelled, update recommendationHistory
              dispatch(
                graphqlApi.util.updateQueryData(endpointName, queryArgs, (draft) => {
                  if (!draft?.data) return;
                  draft.data.readers = draft.data.readers?.map((r) =>
                    r.readerEmployeeId === reviewerEmployeeId
                      ? { ...r, status: 'CANCELLED', data: 'CANCELLED' }
                      : r
                  );
                  draft.data.recommendationHistory = draft.data.recommendationHistory?.map((r) =>
                    r.reviewerEmployeeId === reviewerEmployeeId
                      ? {
                          ...r,
                          recommendation: recommendation || 'Unassigned',
                          recommendationUnified: recommendation || 'Unassigned',
                        }
                      : r
                  );
                })
              );
            } else {
              // ASSIGN — add new reader and recommendationHistory entry
              const assignTime = new Date();
              dispatch(
                graphqlApi.util.updateQueryData(endpointName, queryArgs, (draft) => {
                  if (!draft?.data) return;
                  if (!draft.data.readers) draft.data.readers = [];
                  if (!draft.data.recommendationHistory) draft.data.recommendationHistory = [];

                  const existingReaderIdx = draft.data.readers.findIndex(
                    (r) => r.readerEmployeeId === reviewerEmployeeId
                  );
                  if (existingReaderIdx >= 0) {
                    draft.data.readers[existingReaderIdx] = {
                      ...draft.data.readers[existingReaderIdx],
                      readerFirstName: firstName,
                      readerLastName: lastName,
                      status: 'ASSIGNED',
                      data: undefined,
                    };
                  } else {
                    draft.data.readers.push({
                      readerEmployeeId: reviewerEmployeeId,
                      readerFirstName: firstName,
                      readerLastName: lastName,
                      status: 'ASSIGNED',
                    });
                  }

                  const existingRecIdx = draft.data.recommendationHistory.findIndex(
                    (r) => r.reviewerEmployeeId === reviewerEmployeeId
                  );
                  const recommendationPayload = {
                    reviewerEmployeeId,
                    assignTime,
                    assignTimeFormatted: formatDatetime(assignTime),
                    comments: {
                      adminOverride: null,
                      document: [],
                      evaluation: [],
                      othersConcerns: [],
                    },
                    firstName,
                    lastName,
                    nameFormatted: `${firstName} ${lastName}`,
                    recommendation: recommendation || 'Pending',
                    recommendationUnified: recommendation || 'Pending',
                    stage: stage || '-',
                    submitTime: null,
                    type: null,
                  };

                  if (existingRecIdx >= 0) {
                    draft.data.recommendationHistory[existingRecIdx] = {
                      ...draft.data.recommendationHistory[existingRecIdx],
                      ...recommendationPayload,
                    };
                  } else {
                    draft.data.recommendationHistory.push(recommendationPayload);
                  }
                })
              );
            }
          }

          // Keep cache aligned with backend truth after optimistic patching.
          dispatch(
            graphqlApi.util.invalidateTags([
              { type: 'Applicant', id: applicantId },
            ])
          );
        },
        error: (error) => console.warn('Subscription error:', error),
      });

    return () => {
      sub.unsubscribe();
    };
  }, [applicantId, admitTerm, committeeId, isTraining, dispatch, queryArgs]);

  const name = studentData?.data?.demographics?.firstName ? 
    studentData?.data?.demographics?.firstName +
    " " +
    studentData?.data?.demographics?.middleInitial +
    " " +
    studentData?.data?.demographics?.lastName
    : null;
  
  useEffect(() => {
    if(studentData?.data?.decisionOverride) {
      setDecision(studentData?.data?.decisionOverride)
    } else if (studentData?.data?.decision) {
      setDecision(studentData?.data?.decision)
    }
  }, [studentData?.data?.decision]);

  const status = studentData?.status;
  let loading = isApplicationLoading || status === STATUS.LOADING;
  let isLoadingError = (isApplicationError || status === STATUS.ERROR || status === 'failed') && !submissionInProgressRef.current;

  const refreshDocuments = () => refetchDocuments();

  useEffect(() => {
    if (submitState?.status === STATUS?.SUCCESS && (submitState?.type === 'SUBMIT_RECOMMENDATION')) {
      setOpen(false);
      setOpenSuccess(true);
      setSubmitError(false);
      setNotesError(false);
    }
  }, [submitState]);

  const [decision, setDecision] = useState("");
  const [noteState, noteDispatch] = useReducer(formsReducer, []);

  const renderInputControls = (notes, section, requirementInstructionsEle, invisibleEle) => {
    let fields = [];
    let parentLabelExistsAndIsChecked = false;

    for (let note of notes) {
      const noteStateItem = noteState.find(item => item.label === note.label && item.section === section);
      const parentLabelExists = note?.parentLabel ? true : false;
      const parentLabelExistsAndIsNotChecked = parentLabelExists && !noteState.find(item => item.label === note?.parentLabel)?.value ? true : false;
      
      parentLabelExistsAndIsChecked = parentLabelExists && !parentLabelExistsAndIsNotChecked;
      const asteriskAfterLabel = parentLabelExistsAndIsChecked ? "\u00A0*" : "";
      
      fields.push(
        <Mui.FormControlLabel
          control={
            <Mui.Checkbox
              checked={!!(noteStateItem?.value)}
              onChange={!readOnly ? (val) => noteDispatch({...val, section: section}) : undefined}
              color="primary"
              name={note.label}
              disabled={sectionIsDisabled(studentData?.data?.notes, noteState, section, !!(noteStateItem?.value)) || parentLabelExistsAndIsNotChecked}
              style={{
                paddingLeft: note?.parentLabel && '2rem',
                color: note?.parentLabel && noteState.find(item => item.label === note?.parentLabel)?.value && !exportActionVariable(noteState, noteStateItem?.section).find((item) => item.parentLabel && item.value) && 'red'
              }}
              />
            }
          key={note.label}
          label={`${note.label}${asteriskAfterLabel}`}
        />
      );
    }

    fields = fields.sort((a, b) => {
      if (a.key.startsWith("Displays") && !b.key.startsWith("Displays")) {
        return 1;
      } else if (!a.key.startsWith("Displays") && b.key.startsWith("Displays")) {
        return -1;
      } else {
        return a.key.localeCompare(b.key);
      }
    });
    
    if (requirementInstructionsEle && parentLabelExistsAndIsChecked) {
        fields.push(requirementInstructionsEle);
    } else {
        fields.push(invisibleEle);
    }
    return fields;
  }

  const handleCloseSuccess = () => {
    setOpenSuccess(false);
    setOpenAlertSuccess(false);
    dispatch(submitRecommendation({
      status: null,
      alert: ERROR_CONSTS?.UI?.GENERIC?.EMPTY_ERROR_OBJECT,
      details: '',
    }));
    if(!nextApp)
      navigate("/");
    else
      navigate(`/applicant/${nextApp?.applicationid}/${nextApp?.committeeId}/${nextApp?.term}?todo=${encodedApps}&from=${from}`);
  };
  const [openAlertSuccess, setOpenAlertSuccess] = useState(false);

  const handleSubmit = () => {  
    const checkedRestriction = exportStringArray(noteState, "restrictions").toString();
    if (isOverrideDecision) {
      dispatch(overrideRecommendationAction({ status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT }));
      overrideRecommendation({
        applicantEmployeeId: studentData?.data?.demographics?.applicantEmployeeId,
        committeeId: studentData?.data?.demographics?.committeeId,
        term: studentData?.data?.demographics?.admitTerm?.key,
        recommendation: decision,
        restriction: checkedRestriction,
        comments: adminReason,
      }).unwrap().then((result) => {
        dispatch(overrideRecommendationAction({ status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Override submitted successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } }));
        setIsOverrideDecision(false);
        setAdminReason("");
        setShow(false);
      }).catch((error) => {
        dispatch(overrideRecommendationAction({ status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } }));
      });
    } else {    
      setPageAppHeader(decision);

      const checkedCompetitive = exportStringArray(noteState, "competitive");
      const checkedNoncompetitive = exportStringArray(noteState, "noncompetitive");
      const checkedDocument = exportStringArray(noteState, "document");
      const checkedEvaluation = exportStringArray(noteState, "evaluation");
      const checkedOther = exportStringArray(noteState, "othersConcerns");      

      if (decision === RECOMMENDATIONS?.ADMIT_WITH_RESTRICTION && checkedRestriction.length === 0) {
        dispatch(submitRecommendation({
          status: STATUS.ERROR,
          alert: {
            MESSAGE: "Please choose a restriction from the list.",
            SEVERITY: SEVERITY_LEVELS.WARNING,
          },
        }));
        return;
      }

      const reviewPayload = {
        admitTerm: studentData?.data?.demographics?.admitTerm?.key,
        applicantEmployeeId: studentData?.data?.demographics?.applicantEmployeeId,
        committeeId: studentData?.data?.demographics?.committeeId,
        recommendation: decision,
        restriction: checkedRestriction,
        reviewNotes: {
          competitive: checkedCompetitive,
          document: checkedDocument,
          evaluation: checkedEvaluation,
          noncompetitive: checkedNoncompetitive,
          othersConcerns: checkedOther,
        },
      };

      if (location?.pathname.includes('/training/')) {
        submissionInProgressRef.current = true;
        dispatch(submitRecommendation({ status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT }));
        submitTrainingTrigger({ ...reviewPayload, isTraining: true })
          .unwrap().then((result) => {
            const expectedRecommendation = result?.context?.[0]?.value;
            const submittedRecommendation = result?.context?.[1]?.value;

            if (submittedRecommendation === expectedRecommendation) {
              dispatch(submitRecommendation({ status: STATUS.SUCCESS, context: result?.context, alert: { MESSAGE: 'Congratulations! Your recommendation matched the expected recommendation.', SEVERITY: SEVERITY_LEVELS.SUCCESS }, type: 'SUBMIT_RECOMMENDATION' }));
            } else if (expectedRecommendation === "None" && submittedRecommendation !== "") {
              dispatch(submitRecommendation({ status: STATUS.SUCCESS, context: result?.context, alert: { MESSAGE: 'Your recommendation was submitted successfully.', SEVERITY: SEVERITY_LEVELS.SUCCESS }, type: 'SUBMIT_RECOMMENDATION' }));
            } else {
              dispatch(submitRecommendation({ status: STATUS.SUCCESS, context: result?.context, alert: { MESSAGE: 'Your recommendation did not match the expected recommendation.', SEVERITY: SEVERITY_LEVELS.WARNING }, type: 'SUBMIT_RECOMMENDATION' }));
            }
          }).catch((error) => {
            dispatch(submitRecommendation({ status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } }));
          });
      } else {
        submissionInProgressRef.current = true;
        dispatch(submitRecommendation({ status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT }));
        submitReviewTrigger(reviewPayload)
          .unwrap().then((result) => {
            dispatch(submitRecommendation({ status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Review submitted successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS }, type: 'SUBMIT_RECOMMENDATION' }));
          }).catch((error) => {
            dispatch(submitRecommendation({ status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } }));
          });
      }
    }
  };

  useEffect(() => {
    if (studentData) {
      if (openSuccess) {
        return;
      }
      noteDispatch({ type: "RESET" });
      noteDispatch({ type: "ADD", payload: studentData?.data?.notes?.competitive, section: "competitive" });
      noteDispatch({ type: "ADD", payload: studentData?.data?.notes?.noncompetitive, section: "noncompetitive" });
      noteDispatch({ type: "ADD", payload: studentData?.data?.notes?.document, section: "document"  });
      noteDispatch({ type: "ADD", payload: studentData?.data?.notes?.evaluation, section: "evaluation" });
      noteDispatch({ type: "ADD", payload: studentData?.data?.notes?.othersConcerns, section: "othersConcerns" });
   
      let currentReviewer = studentData?.data?.readers?.find((reviewer) => reviewer.readerEmployeeId === userId && [STATUS.ASSIGNED, STATUS.RESUMELATER].includes(reviewer.status));
      if (currentReviewer) {
        for (let objItem in currentReviewer.reviewNotes) {
          for (let item of currentReviewer.reviewNotes[objItem] ?? []) {
            noteDispatch({ target: { checked: true, name: item.label, type: "checkbox" }, section: objItem })
          }
        }
      }
    }
  }, [studentData, userId, openSuccess]);

  const displayNoCompetitive = noteState.find((item) => {
    return item.section === "competitive" && item.label === "Displays none of these attributes";
  });
  const displayNoNoncompetitive = noteState.find((item) => {
    return item.section === "noncompetitive" && item.label === "Displays none of these attributes";
  });
  
  useEffect(() => {
    if (displayNoCompetitive?.value === true) {
      noteState?.map((item) => {
        if ((item.section === "competitive" && item.label !== "Displays none of these attributes")) { 
        item.value = false;     
        }
        noteDispatch({ target: { checked: false, name: item.label, section: "competitive",  type: "checkbox" } })
      });
    }
  }, [displayNoCompetitive?.value]);

  useEffect(() => {
    if (displayNoNoncompetitive?.value === true) {
      noteState?.map((item) => {
        if ((item.section === "noncompetitive" && item.label !== "Displays none of these attributes")) { 
        item.value = false;     
        }
        noteDispatch({ target: { checked: false, name: item.label, section: "noncompetitive",  type: "checkbox" } })
      });
    }
  }, [displayNoNoncompetitive?.value]);

  const isCompetitiveChecked = noteState?.some((item) => {
    const isCompChecked = item.value;
    const isCompetitive = item.section === "competitive";
    return isCompetitive && isCompChecked;
  });
    
  const isNoncompetitiveChecked = noteState?.some((item) => {
    const isNoncompChecked = item.value;
    const isNoncompetitive = item.section === "noncompetitive";
    return isNoncompetitive && isNoncompChecked;
  });
  const areCheckboxesChecked = (isCompetitiveChecked || isNoncompetitiveChecked);

  const competitiveCheckboxesChecked = noteState?.some((item) => {
    const isChecked = item.value;
    const isCompetitive = item.section === "competitive";
    return (
      isCompetitive && item !== displayNoCompetitive && isChecked
    );
  });
  const noncompetitiveCheckboxesChecked = noteState?.some((item) => {
    const isChecked = item.value;
    const isNoncompetitive = item.section === "noncompetitive";
    return (
      isNoncompetitive && item !== displayNoNoncompetitive && isChecked
    );
  });
      
  useEffect(() => {
    if (competitiveCheckboxesChecked === true) {
      noteState?.map((item) => {
        if ((item.section === "competitive" && item.label === "Displays none of these attributes")) { 
          item.value = false;     
        }
        noteDispatch({ target: { checked: false, name: item.label, type: "checkbox" } })
      });
    }
    if (noncompetitiveCheckboxesChecked === true) {
      noteState?.map((item) => {
        if ((item.section === "noncompetitive" && item.label === "Displays none of these attributes")) { 
          item.value = false;     
        }
        noteDispatch({ target: { checked: false, name: item.label, type: "checkbox" } })
      });
    }
  }, [competitiveCheckboxesChecked, noncompetitiveCheckboxesChecked]);

  useEffect(() => {
    if(location.pathname.includes("/applicant/") || location.pathname.includes("/training/"))
      return;
    dispatch(cleanupApplicantDetail());
  }, [location.pathname]);
  
  useEffect(() => {
    if (Array.isArray(documentFiles?.data) && documentFiles?.data?.length > 0) {
      for (let doc of documentFiles?.data) {
        if (doc.status !== STATUS.SUCCESS) { continue; }
        window.open(doc.signedUrl, "_blank");
      }
    }
  }, [documentFiles]);

  const term = studentData?.data?.demographics?.admitTerm;

  const creditHours = studentData?.data?.universityInfo?.map?.((item) => parseInt(item.completedUnits));
  const totalCreditHours = creditHours?.reduce((a, b) => a + b, 0);
  const defaultCreditHours = import.meta.env.VITE_CREDIT_HOURS ?? 30;
  const applicationStatus = studentData?.data?.demographics?.status;
  const customData = { authDetails, applicationStatus, isTraining, location };
  const sectionsFiltered = (committee === "international-transfer" || committee === "domestic-transfer") && totalCreditHours >= defaultCreditHours ? sections?.filterAuth(authDetails, customData)?.filter((item) => !["collegePrep", "highschoolUnits"]?.includes(item.id)) : sections?.filterCombined(authDetails, committee, customData);

  useEffect(() => {
    if (authDetails?.ADMIN === true && authDetails?.ADMIN_REVIEWER !== true && applicationStatus !== "ADMINREVIEW") {
      setShow(false);
    }
    else {
      setShow(true);
    }
  }, [authDetails?.ADMIN, authDetails?.ADMIN_REVIEWER, applicationStatus]);

  const handleUnlock = () => {
    setShow(true);
    setIsOverrideDecision(true);
    scrollToHash(document, "recommendation-heading");
  };
  
  const handleDecision = (value) => {
    setDecision(value);
    setSubmitError(false);
  };
  const [open, setOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const handleCloseBackDrop = () => {
    setOpen(false);
    setOpenAlert(false);
  };
  const handleAlert = () => {
    setOpenAlert(false);
    setOpenAlertSuccess(true);
  };
  const [submitError, setSubmitError] = useState(false);
  const [notesError, setNotesError] = useState(false);

  const college= Object.keys(noteState).find((item) => item === "College Prep Units Appear Inaccurate");
  const other= Object.keys(noteState).find((item) => item === "Other Document Inaccurate");

  useEffect(() => {
    for(let item in noteState) { 
      if((item === college || item === other) && noteState[item].value === false)  
        setNotesError(false);              
    } 
  }, [college, other, noteState]);

  useEffect(() => {
    if (validate(noteState) ) {
      setNotesError(false);
    }   
  }, [noteState]);

  const handleToggle = useCallback(() => {  
    let notesClear = false
    if (!validate(noteState)) {
      setNotesError(true);
      submitErrorLabelRef.current.focus()
    } else {
      notesClear = true
    }   
    if (notesClear) {
      if (decision === "") {
        setSubmitError(true);
        submitErrorLabelRef.current.focus()
      } else if (isOverrideDecision && adminReason === "")  {
        setSubmitError(true);
        submitErrorLabelRef.current.focus();
      } else if (!areCheckboxesChecked && !isOverrideDecision)  {
        setSubmitError(true);
        submitErrorLabelRef.current.focus();
      } else {
        setOpen(true);
      }
    }
  }, [noteState, decision, adminReason, isOverrideDecision]);

  const recHistory = studentData?.data?.recommendationHistory;
  let approvalStage = null;
  let reviewerEmployeeId = null;

  if (Array.isArray(recHistory) && recHistory.length > 0) {
    const validObjs = recHistory.filter(obj => obj && obj.stage !== undefined && obj.stage !== null);
    if (validObjs.length > 0) {
      const maxStage = Math.max(...validObjs.map(obj => Number(obj.stage)));
      const maxStageObjs = validObjs.filter(obj => Number(obj.stage) === maxStage);
      const preferredObj = maxStageObjs.find(obj => obj.recommendation === "Pending") || maxStageObjs[0];
      approvalStage = preferredObj?.stage ?? null;
      reviewerEmployeeId = preferredObj?.reviewerEmployeeId ?? null;
    }
  }

  // Wrap RTK Query mutation triggers to match the prop signatures expected by child components
  const toggleHoldForExport = useCallback((applicantEmployeeId, committeeIdArg, termArg, holdForExportArg) => {
    dispatch(toggleHoldForExportAction({ status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT }));
    holdForExportTrigger({ applicantEmployeeId, committeeId: committeeIdArg, term: termArg, holdForExport: holdForExportArg })
      .unwrap().then((result) => {
        dispatch(toggleHoldForExportAction({ status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Hold for export updated', SEVERITY: SEVERITY_LEVELS.SUCCESS } }));
      }).catch((error) => {
        dispatch(toggleHoldForExportAction({ status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } }));
      });
  }, [holdForExportTrigger, dispatch]);

  const toggleHoldForAdmin = useCallback((applicantEmployeeId, committeeIdArg, termArg, holdForAdminArg) => {
    dispatch(toggleHoldForAdminAction({ status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT }));
    holdForAdminTrigger({ applicantEmployeeId, committeeId: committeeIdArg, term: termArg, holdForAdmin: holdForAdminArg })
      .unwrap().then((result) => {
        dispatch(toggleHoldForAdminAction({ status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Hold for admin updated', SEVERITY: SEVERITY_LEVELS.SUCCESS } }));
      }).catch((error) => {
        dispatch(toggleHoldForAdminAction({ status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } }));
      });
  }, [holdForAdminTrigger, dispatch]);

  // Document downloads using RTK Query lazy queries — replaces legacy getDocumentAction
  const getDocument = useCallback(async (documents, admitTermArg, applicantEmployeeIdArg, committeeIdArg, queryKey) => {
    if (!Array.isArray(documents) || documents.length < 1) return;

    const datetimeDownloadRequested = new Date().toISOString();
    dispatch(getDocumentSliceAction({
      status: STATUS.LOADING,
      documents,
      datetimeDownloadRequested,
    }));

    let requestCount = documents.length;
    let requestSuccessCount = 0;

    try {
      const fetchFn = queryKey === 'getApplicationDocument'
        ? fetchAppDoc
        : queryKey === 'getApplicationDocumentReadOnly'
          ? fetchAppDocReadOnly
          : fetchTrainingDoc;

      const responses = await Promise.all(
        documents.map(async (doc) => {
          try {
            const variables = queryKey === 'getApplicationDocumentReadOnly'
              ? { documentId: doc.documentId, documentName: doc.documentName, applicantEmployeeId: applicantEmployeeIdArg }
              : { documentId: doc.documentId, documentName: doc.documentName, admitTerm: admitTermArg, applicantEmployeeId: applicantEmployeeIdArg, committeeId: committeeIdArg };

            const result = await fetchFn(variables).unwrap();
            const signedUrl = result?.data?.signedUrl;
            if (typeof signedUrl !== 'string') {
              if (requestCount > 1) await new Promise(r => setTimeout(r, 3000));
              throw new Error(`Invalid signed url, ${signedUrl}`);
            }
            requestSuccessCount++;
            return { status: STATUS.SUCCESS, signedUrl };
          } catch (exception) {
            if (requestCount === 1) throw exception;
            console.error('Error while retrieving document', doc, doc.documentId, doc.documentName);
            return { status: STATUS.ERROR, documentId: doc.documentId, documentName: doc.documentName };
          }
        })
      );

      const allSucceeded = requestSuccessCount === requestCount;
      dispatch(getDocumentSliceAction({
        alert: allSucceeded ? undefined : {
          ALERT_BAR_MESSAGE: `Error retrieving ${requestCount - requestSuccessCount} of ${requestCount} documents`,
          MESSAGE: `Error retrieving ${requestCount - requestSuccessCount} of ${requestCount} documents`,
          SEVERITY: SEVERITY_LEVELS.ERROR,
        },
        status: allSucceeded ? STATUS.SUCCESS : STATUS.ERROR,
        data: responses,
        datetimeDownloadRequested,
      }));
    } catch (error) {
      console.error('Error retrieving documents', error);
      const errorMessage = requestCount <= 1
        ? 'Error retrieving document'
        : `Error retrieving ${requestCount - requestSuccessCount} of ${requestCount} documents`;

      dispatch(getDocumentSliceAction({
        status: STATUS.ERROR,
        datetimeDownloadRequested,
        alert: {
          ALERT_BAR_MESSAGE: errorMessage,
          MESSAGE: error?.errors?.[0]?.message || error?.message || 'Unexpected Error',
          SEVERITY: SEVERITY_LEVELS.ERROR,
        },
      }));
    }
  }, [dispatch, fetchAppDoc, fetchAppDocReadOnly, fetchTrainingDoc]);

  useEffect(() => {
    setPageAppHeader({
      name,
      committee: studentData?.data?.demographics?.committeeId,
      committeeName: studentData?.data?.committeeDetails?.committeeName,
      emplId: studentData?.data?.demographics?.applicantEmployeeId,
      dateOfBirth: studentData?.data?.demographics?.dateOfBirth,
      stage: studentData?.data?.demographics?.stage,
      approvalStage: approvalStage,
      status: studentData?.data?.demographics?.status,
      reviewerEmployeeId: reviewerEmployeeId,
      restriction: studentData?.data?.readers?.find((reviewer) => reviewer.readerEmployeeId === userId)?.restriction,
      unlock: handleUnlock,
      authDetails,
      decision,
      noteState,
      userId,
      handleToggle,
      handleDecision,
      handleSubmit,
      loading,
      priority: studentData?.data?.priority,
      term,
      applicantTrainingExists: trainingConfig?.trainingExists
    });
  }, [authDetails, decision, handleToggle, handleDecision, handleSubmit, committee, loading, name, noteState, setPageAppHeader, term, trainingConfig?.trainingExists, approvalStage, reviewerEmployeeId]);
  
  useEffect(() => {
    // SAS-31 SAS-32, prevent documents from re-opening and unauthorized pages for SLT users
    return () => dispatch(cleanupApplicantDetail());
  }, []);

  return (
    <>
      {loading && (
        <Mui.Box>
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" height="64px"></Mui.Skeleton>
          <br />
          <Mui.Skeleton variant="rectangular" width={100} height={"1em"}></Mui.Skeleton>
        </Mui.Box>
      )}
      {!loading && isLoadingError && (
        <Mui.Alert severity="error">
          {studentData?.error?.message || "Unable to load application. You may not have access to this applicant."}
        </Mui.Alert>
      )}
      {!loading && !isLoadingError && (
        <>
          {isApplicationFetching && (
            <Mui.LinearProgress sx={{ mb: 1 }} />
          )}
          {sortBySortPriority(sectionsFiltered).map(
            (
              { id, heading, sortPriority, Component: ChildComponent, defaultCollapsed },
              index
            ) => (
              <TOC.Section
                expandable
                defaultExpanded={!sections?.checkDefaultCollapsed(defaultCollapsed, authDetails)}
                sx={styles.root}
                id={id}
                key={`${index}${id}-jumpsection`}
                heading={heading}
                sortPriority={sortPriority}
                accordionSummaryProps={{
                  sx: styles.expanded
                }}
                AnchorHeadingProps={{
                  variant:  "h5",
                  component: "h2"
                }}
              >
                  <ChildComponent
                    handleUnlock={handleUnlock}
                    name={name}
                    committee={committee}
                    studentData={studentData}
                    decision={decision}
                    open={open}
                    setOpen={setOpen}
                    openAlert={openAlert}
                    show={show}
                    setShow={setShow}
                    handleDecision={handleDecision}
                    handleCloseBackDrop={handleCloseBackDrop}
                    handleCloseSuccess={handleCloseSuccess}
                    handleToggle={handleToggle}
                    handleSubmit={handleSubmit}
                    handleAlert={handleAlert}
                    readOnly={readOnly}
                    notifyAdmin={notifyAdmin}
                    userIsReader={userIsReader}
                    openSuccess={openSuccess}
                    openAlertSuccess={openAlertSuccess}
                    submitError={submitError}
                    notesError={notesError}
                    areCheckboxesChecked={areCheckboxesChecked}
                    setSubmitError={setSubmitError}
                    isOverrideDecision={isOverrideDecision}
                    noteState={noteState}
                    noteDispatch={noteDispatch}
                    renderInputControls={renderInputControls}
                    applicationDocuments={applicationDocuments}
                    documentFiles={documentFiles}
                    getDocument={getDocument}
                    isTraining={isTraining}
                    adminReason={adminReason}
                    setAdminReason={setAdminReason}
                    applicationStatus={applicationStatus}
                    toggleHoldForExport={toggleHoldForExport}
                    toggleHoldForAdmin={toggleHoldForAdmin}
                    admitTerm={admitTerm}
                    toggleHoldForExportState={toggleHoldForExportState}
                    encodedApps={encodedApps}
                    nextApp={nextApp}
                    getExpectedRecommendation={null}
                    refreshDocuments={refreshDocuments}
                    submitErrorLabelRef={submitErrorLabelRef}
                  />
                </TOC.Section>
            )
          )}
        </>
      )}
    </>
  );
}

export default Applicant;
